import React from "react";
import { cn } from "@/lib/utils";

export type ChipTone = "lavender" | "peach" | "rose" | "sky" | "mint" | "lilac" | "muted";

export const CHIP_TONES: Record<ChipTone, string> = {
  lavender: "bg-pastel-lavender text-pastel-lavender-ink",
  peach: "bg-pastel-peach text-pastel-peach-ink",
  rose: "bg-pastel-rose text-pastel-rose-ink",
  sky: "bg-pastel-sky text-pastel-sky-ink",
  mint: "bg-pastel-mint text-pastel-mint-ink",
  lilac: "bg-pastel-lilac text-pastel-lilac-ink",
  muted: "bg-muted text-body-strong",
};

/** Maps a backend status to the pastel tone used for its chip. */
export function toneForStatus(status?: string | null): ChipTone {
  const s = (status || "").toLowerCase();
  if (/(approv|verif|accept|complete|paid|success|present|active)/.test(s)) return "mint";
  if (/(reject|fail|declin|cancel|absent|suspend|overdue)/.test(s)) return "rose";
  if (/(pending|review|submit|wait|process)/.test(s)) return "peach";
  if (/(draft|new)/.test(s)) return "lilac";
  if (/(schedul|interview|progress|open)/.test(s)) return "sky";
  return "lavender";
}

interface EventChipProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  tone?: ChipTone;
  icon?: React.ReactNode;
  title: React.ReactNode;
  meta?: React.ReactNode;
  /** Render as a button (for chips that open something). */
  asButton?: boolean;
}

/**
 * A pastel activity chip: a white icon tile, a title and a small meta line.
 * Used for calendar events, work-log entries and timeline items.
 */
export function EventChip({
  tone = "lavender",
  icon,
  title,
  meta,
  asButton,
  className,
  ...props
}: EventChipProps) {
  const Comp: any = asButton ? "button" : "div";
  return (
    <Comp
      type={asButton ? "button" : undefined}
      className={cn(
        "inline-flex max-w-full min-w-0 items-center gap-2.5 rounded-xl py-1.5 pr-4 pl-1.5 text-left",
        asButton &&
          "transition-[transform,box-shadow] hover:-translate-y-px hover:shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        CHIP_TONES[tone],
        className,
      )}
      {...props}
    >
      {icon && (
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-card text-foreground shadow-[0_1px_2px_rgb(0_0_0/0.06)] [&_svg]:size-4"
          aria-hidden
        >
          {icon}
        </span>
      )}
      <span className={cn("min-w-0", !icon && "pl-2.5")}>
        <span className="block truncate text-[13px] leading-[18px] font-medium">{title}</span>
        {meta && <span className="block truncate text-xs leading-4 opacity-70">{meta}</span>}
      </span>
    </Comp>
  );
}

export default EventChip;
